import { configureStore } from '@reduxjs/toolkit';
import { applicationAPI } from './services/client/profile';
import application from './slices/application';
import onboarding from './slices/onboarding';
import chatMessage from './slices/chatMessage';
import cocFocus from './slices/cocFocus';
import aiProfileSync from './slices/aiProfileSync';
import userRole from './slices/userRole';
import loanTools from './slices/loanTools';
import authReducer from './slices/authSlice';
import { onboardingAPI } from './services/client/onboarding';
import { documentUploadAPI } from './services/client/documentUpload';
import { pipelineAPI } from './services/client/pipeline';
import { bulkUploadAPI } from './services/client/bulkUpload';
import { documentAPI } from './services/client/document';
import { dashboardAPI } from './services/client/dashboard';
import { truvConsumerAPI } from './services/client/truv';
import { loanInformationAPI } from './services/admin/loanInformation';
import { borrowerProfileAPI } from './services/borrower-profile';
import { chatAPI } from './services/chatHistory';
import { loanProcessorAPI } from './services/loanProcessor';
import { passwordAPI } from './services/update-password';
import { adminFeesworksheetAPI } from './services/admin/feesworksheet';
import { disclosuresAPI } from './services/admin/disclosures';
import { rbacAPI } from './services/admin/rbac';
import { adminPipelineAPI } from './services/admin/pipeline';
import { productAndPricingAPI } from './services/admin/product-pricing';
import { adminConsumerAPI } from './services/admin/adminConsumer';
import { adminEmployeeAPI } from './services/admin/adminEmployee';
import { adminDocumentAPI } from './services/admin/document';
import { adminToolsAPI } from './services/admin/adminTools';
import { quickPriceAPI } from './services/admin/quickPrice';
import { reportsAPI } from './services/admin/reports';
import { notificationsAPI } from './services/notifications';
import { supportAPI } from './services/support';
import { qualityCheckAPI } from './services/admin/qualityCheck';
import { preQcAPI } from './services/admin/preQc';
import { dscrAPI } from './services/admin/dscr';
import { whatsNextAPI, whatsNextRefreshMiddleware } from './services/admin/whatsNext';
import { userManagementV2API } from './services/admin/userManagementV2';
import { branchManagementAPI } from './services/admin/branchManagement';
import { toolsApi } from './services/tools';
import { pricingIntegrationsApi } from './services/integrations/pricing';
import { consumerToolsAPI } from './services/client/tools';
import { compositeInitAPI } from './services/client/compositeInit';
import { underwritingAPI } from './services/admin/underwriting';
import { docProcessingAPI } from './services/client/docProcessing';
import { serviceOrderingAPI } from './services/serviceOrdering';

// Invalidate the composite dashboard cache when related mutations complete.
// Only invalidate for mutations that actually change dashboard-visible data
// (pipeline status, documents). Profile section edits don't affect the lean
// dashboard payload, so 'profile' is excluded to avoid unnecessary refetches
// that cause DB contention when the user is editing on the profile page.
const INVALIDATION_API_NAMES = ['pipeline', 'Document', 'documentUploadAPI'];
const compositeInvalidationMiddleware = (storeAPI) => (next) => (action) => {
  const result = next(action);
  if (action.type?.endsWith('/fulfilled') && action.meta?.arg?.type === 'mutation') {
    const apiName = action.type.split('/')[0];
    if (INVALIDATION_API_NAMES.includes(apiName)) {
      storeAPI.dispatch(compositeInitAPI.util.invalidateTags(['dashboardInit']));
    }
  }
  return result;
};

export const store = configureStore({
  reducer: {
    auth: authReducer,
    application,
    onboard: onboarding,
    chatMessage,
    cocFocus,
    aiProfileSync,
    userRole,
    loanTools,
    [onboardingAPI.reducerPath]: onboardingAPI.reducer,
    [applicationAPI.reducerPath]: applicationAPI.reducer,
    [pipelineAPI.reducerPath]: pipelineAPI.reducer,
    [bulkUploadAPI.reducerPath]: bulkUploadAPI.reducer,
    [documentUploadAPI.reducerPath]: documentUploadAPI.reducer,
    [documentAPI.reducerPath]: documentAPI.reducer,
    [dashboardAPI.reducerPath]: dashboardAPI.reducer,
    [truvConsumerAPI.reducerPath]: truvConsumerAPI.reducer,
    [loanInformationAPI.reducerPath]: loanInformationAPI.reducer,
    [adminFeesworksheetAPI.reducerPath]: adminFeesworksheetAPI.reducer,
    [disclosuresAPI.reducerPath]: disclosuresAPI.reducer,
    [rbacAPI.reducerPath]: rbacAPI.reducer,
    [borrowerProfileAPI.reducerPath]: borrowerProfileAPI.reducer,
    [chatAPI.reducerPath]: chatAPI.reducer,
    [loanProcessorAPI.reducerPath]: loanProcessorAPI.reducer,
    [passwordAPI.reducerPath]: passwordAPI.reducer,
    [adminPipelineAPI.reducerPath]: adminPipelineAPI.reducer,
    [productAndPricingAPI.reducerPath]: productAndPricingAPI.reducer,
    [qualityCheckAPI.reducerPath]: qualityCheckAPI.reducer,
    [preQcAPI.reducerPath]: preQcAPI.reducer,
    [dscrAPI.reducerPath]: dscrAPI.reducer,
    [whatsNextAPI.reducerPath]: whatsNextAPI.reducer,
    [adminConsumerAPI.reducerPath]: adminConsumerAPI.reducer,
    [adminEmployeeAPI.reducerPath]: adminEmployeeAPI.reducer,
    [adminDocumentAPI.reducerPath]: adminDocumentAPI.reducer,
    [adminToolsAPI.reducerPath]: adminToolsAPI.reducer,
    [quickPriceAPI.reducerPath]: quickPriceAPI.reducer,
    [reportsAPI.reducerPath]: reportsAPI.reducer,
    [notificationsAPI.reducerPath]: notificationsAPI.reducer,
    [supportAPI.reducerPath]: supportAPI.reducer,
    [userManagementV2API.reducerPath]: userManagementV2API.reducer,
    [branchManagementAPI.reducerPath]: branchManagementAPI.reducer,
    [pricingIntegrationsApi.reducerPath]: pricingIntegrationsApi.reducer,
    [toolsApi.reducerPath]: toolsApi.reducer,
    [consumerToolsAPI.reducerPath]: consumerToolsAPI.reducer,
    [compositeInitAPI.reducerPath]: compositeInitAPI.reducer,
    [underwritingAPI.reducerPath]: underwritingAPI.reducer,
    [docProcessingAPI.reducerPath]: docProcessingAPI.reducer,
    [serviceOrderingAPI.reducerPath]: serviceOrderingAPI.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      onboardingAPI.middleware,
      applicationAPI.middleware,
      pipelineAPI.middleware,
      bulkUploadAPI.middleware,
      documentUploadAPI.middleware,
      documentAPI.middleware,
      dashboardAPI.middleware,
      truvConsumerAPI.middleware,
      loanInformationAPI.middleware,
      borrowerProfileAPI.middleware,
      chatAPI.middleware,
      loanProcessorAPI.middleware,
      passwordAPI.middleware,
      adminFeesworksheetAPI.middleware,
      disclosuresAPI.middleware,
      rbacAPI.middleware,
      adminPipelineAPI.middleware,
      qualityCheckAPI.middleware,
      preQcAPI.middleware,
      dscrAPI.middleware,
      whatsNextAPI.middleware,
      whatsNextRefreshMiddleware,
      productAndPricingAPI.middleware,
      adminConsumerAPI.middleware,
      adminEmployeeAPI.middleware,
      adminDocumentAPI.middleware,
      reportsAPI.middleware,
      adminToolsAPI.middleware,
      quickPriceAPI.middleware,
      notificationsAPI.middleware,
      supportAPI.middleware,
      userManagementV2API.middleware,
      branchManagementAPI.middleware,
      pricingIntegrationsApi.middleware,
      toolsApi.middleware,
      consumerToolsAPI.middleware,
      compositeInitAPI.middleware,
      underwritingAPI.middleware,
      docProcessingAPI.middleware,
      serviceOrderingAPI.middleware,
      compositeInvalidationMiddleware
    ),
});
